import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import AdminDeletePostButton from '@/components/AdminDeletePostButton'
import PostComments from '@/components/PostComments'
import SafePostText from '@/components/SafePostText'
import { formatRelativeTime } from '@/lib/format'
import { isHttpImageUrl } from '@/lib/tokenImage'
import type { WalletPost } from '@/lib/walletPosts'

export default function ColonyPostGrid({ posts }: { posts: WalletPost[] }) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const gridPosts = posts.filter(post => post.image && isHttpImageUrl(post.image))
  const textPosts = posts.filter(post => !(post.image && isHttpImageUrl(post.image)))
  const activePost = gridPosts.find(post => post.id === activeId) || null

  useEffect(() => {
    if (!activeId)
      return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
        setActiveId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeId])

  return (
    <>
      {gridPosts.length > 0
        ? (
            <div className="mt-4 grid grid-cols-3 gap-1 sm:gap-2">
              {gridPosts.map(post => (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => setActiveId(post.id)}
                  className="group relative aspect-square overflow-hidden rounded-md bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60 sm:rounded-xl"
                  aria-label="Open photo and comments"
                >
                  <img
                    src={post.image}
                    alt="Colony profile photo"
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          )
        : null}

      {textPosts.length > 0
        ? (
            <ul className="mt-4 divide-y divide-white/5">
              {textPosts.map(post => (
                <li key={post.id} className="py-3 first:pt-0 last:pb-0">
                  <p className="text-[11px] text-zinc-500">{formatRelativeTime(post.createdAt)}</p>
                  <SafePostText text={post.caption} extraLink={post.link} className="mt-1" />
                  <div className="mt-2 flex items-center justify-end">
                    <AdminDeletePostButton postKind="wallet" postId={post.id} />
                  </div>
                  <PostComments postKind="wallet" postId={post.id} />
                </li>
              ))}
            </ul>
          )
        : null}

      {activePost
        ? createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Photo and comments"
              onClick={() => setActiveId(null)}
            >
              <div
                className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-zinc-950 p-4"
                onClick={event => event.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-zinc-500">{formatRelativeTime(activePost.createdAt)}</p>
                  <button
                    type="button"
                    onClick={() => setActiveId(null)}
                    className="rounded-full border border-white/15 px-3 py-1 text-xs text-zinc-300 hover:text-white"
                  >
                    Close
                  </button>
                </div>
                <img
                  src={activePost.image}
                  alt="Colony profile photo"
                  className="mt-3 h-auto max-h-[46vh] w-full rounded-2xl object-contain"
                />
                <SafePostText text={activePost.caption} extraLink={activePost.link} className="mt-3" />
                <div className="mt-2 flex items-center justify-end">
                  <AdminDeletePostButton postKind="wallet" postId={activePost.id} />
                </div>
                <PostComments postKind="wallet" postId={activePost.id} />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
